import React, { useContext, useState } from "react";
import { Button, Popover, ListItemText, Typography, ListItemButton, TextField, InputAdornment } from "@mui/material";
import { ExpandMoreOutlined, SearchOutlined } from "@mui/icons-material";
import { FixedSizeList } from "react-window";
import AustraliaFilled from "../IconComponents/AustraliaFilled";
import NewZealandFilled from "../IconComponents/NewZealandFilled";
import PageContext from "../../components/Common/PageContext";
import { useStyles } from "./AppBar.styles";
import { LocationSelectionProps } from "./types";

const POPOVER_HEIGHT = 500;
const FILTER_INPUT_HEIGHT = 48;
const ROW_HEIGHT = 50;
export default function LocationSelection({ locations, onChangeLocationCallback }: LocationSelectionProps) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [filterText, setFilterText] = useState("");
	const classes = useStyles();

	const [pageContext] = useContext(PageContext);
	const { userLocation, userInformation } = pageContext;
	const { countryCode } = userInformation;

	const countryImage = countryCode.toUpperCase() === "AU" ? <AustraliaFilled /> : <NewZealandFilled />;

	const onClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleLocationChangeClick = (value) => {
		onChangeLocationCallback(value);
		onClose();
	};

	const onClose = () => {
		setAnchorEl(null);
		setFilterText("");
	};

	const normalizedFilterText = filterText.trim().toLowerCase();

	const filteredLocations = normalizedFilterText
		? locations.filter(
				(loc) => loc.name.toLowerCase().includes(normalizedFilterText) || String(loc.id).includes(normalizedFilterText)
		  )
		: locations;

	const renderRow = (props) => {
		const { index, style } = props;

		return (
			<ListItemButton
				key={index}
				style={style}
				onClick={(e) => handleLocationChangeClick(String(filteredLocations[index].id))}
				divider
			>
				<ListItemText
					primary={
						<Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>{`${filteredLocations[index].name}`}</Typography>
					}
					secondary={
						<Typography
							variant="caption"
							sx={{ lineHeight: 1.2 }}
						>{`Location ${filteredLocations[index].id}`}</Typography>
					}
				/>
			</ListItemButton>
		);
	};

	return (
		<React.Fragment>
			<Button aria-controls="fade-menu" aria-haspopup="true" onClick={onClick} startIcon={countryImage}>
				{userLocation}
				{locations.length > 1 && <ExpandMoreOutlined />}
			</Button>
			{locations.length > 1 && (
				<Popover
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					onClose={onClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center",
					}}
				>
					<div className={classes.locationSelectContainer}>
						<TextField
							fullWidth
							placeholder="Type to filter..."
							inputProps={{ "aria-label": "Filter locations" }}
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
							autoFocus
							size="small"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlined fontSize="small" />
									</InputAdornment>
								),
							}}
							sx={(theme) => ({
								padding: "8px 8px 4px",
								...(theme.palette.mode === "dark" && {
									"& .MuiOutlinedInput-root": {
										backgroundColor: "rgba(255, 255, 255, 0.1)",
									},
								}),
							})}
						/>
						<FixedSizeList
							width={300}
							height={POPOVER_HEIGHT - FILTER_INPUT_HEIGHT}
							itemSize={ROW_HEIGHT}
							itemCount={filteredLocations.length}
						>
							{renderRow}
						</FixedSizeList>
					</div>
				</Popover>
			)}
		</React.Fragment>
	);
}

export const MemorizedLocationSelection = React.memo(LocationSelection);
